import type { EquipmentItem } from '@sb/integrations/types';
import { RunSkillStep } from '@sb/remote-control/step/runSkill';
import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule, type RoutineView } from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import {
  validateConditional,
  validateStoredConditionShape,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class RunSkillRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.RunLearnedModel', 'RunLearnedModel');
  }

  protected validate({
    args,
    ctx,
    routine,
    warn,
  }: CanonicalValidateArgs): void {
    type RSArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'RunLearnedModel' }
    >;
    const a = args as RSArgs;
    this.checkModelPath(a);
    this.checkGpuTaskRunnerProvided(a);
    const equipmentConfig = this.findEquipment(ctx, a.gpuTaskRunnerId!);
    this.checkEquipmentExists(equipmentConfig, a);
    warn(this.checkCondition(a, routine));
  }

  /**
   * Validate the exit condition's stored shape and references. The condition
   * converts through `buildConditionalFromJSON` when the routine is prepared
   * for the runner, so a condition it rejects must fail here, where the
   * finding can name the problem.
   *
   * @throws {ActionRequiredError}
   */
  private checkCondition(
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
    routine: RoutineView,
  ): WarningFinding | undefined {
    if (!RunSkillStep.hasConditionRows(args.condition)) return undefined;

    validateStoredConditionShape(args.condition);

    const parsed: unknown = JSON.parse(args.condition);
    const conditions = Array.isArray(parsed) ? parsed : [];

    return validateConditional(conditions, routine);
  }

  /**
   * Ensure modelPath is provided.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkModelPath(
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    if (!args.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Model path not specified',
        fieldId: 'modelPath',
      });
    }
  }

  /**
   * Ensure gpuTaskRunnerId is provided.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkGpuTaskRunnerProvided(
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    if (args.gpuTaskRunnerId == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task runner specified',
        fieldId: 'gpuTaskRunnerId',
      });
    }
  }

  private checkEquipmentExists(
    equipmentConfig: EquipmentItem | undefined,
    args: Extract<RcStep.Arguments, { argumentKind: 'RunLearnedModel' }>,
  ): void {
    /**
     * Ensure equipment exists in context.
     * Throws ActionRequiredError if not found.
     *
     * @throws {ActionRequiredError}
     */
    if (!equipmentConfig) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${args.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'gpuTaskRunnerId',
      });
    }
  }
}

export const RunSkillValidation: ValidationRule =
  new RunSkillRule().toValidationRule();
